import React, { useState } from 'react';
import { images as imageUtils } from '../../../utils/api';
import ImageViewerModal from '../../../components/shared/ImageViewerModal';
import { TrackingButton } from './LotCard';

export function LotDetailsModal({ 
  lot, 
  auctionCurrency, 
  certServices, 
  isOpen, 
  onClose, 
  auctionName, 
  auctionHouseName,
  isAuctionSelected,
  onSelect,
  onDeselect,
  onSelectAuction
}) {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (images, index) => {
    setSelectedImage({ images, index, coin: lot });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleNavigate = (newIndex) => {
    setSelectedImage(prev => ({ ...prev, index: newIndex }));
  };
  
  // Get metadata
  const metadata = React.useMemo(() => {
    return lot?.metadata || null;
  }, [lot?.metadata]);

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && !selectedImage) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, selectedImage]);

  if (!isOpen || !lot) return null;

  return (
    <>
      <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col relative z-[60]">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Lot {lot.lot_number} - {auctionHouseName} {auctionName}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {metadata?.lot_url && (
                <a 
                  href={metadata.lot_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-md transition-colors inline-block text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  View on auction site
                </a>
              )}
              <div className="w-full sm:w-40">
                <TrackingButton
                  lotId={lot.id}
                  isTracked={lot.user_selection}
                  isAuctionSelected={isAuctionSelected}
                  onSelect={onSelect}
                  onDeselect={onDeselect}
                  onSelectAuction={onSelectAuction}
                />
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Images */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-4">
              {lot.images?.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((image, index) => (
                <img
                  key={index}
                  src={imageUtils.getImage(image.thumbnail_path, image.source)}
                  alt={`Lot ${lot.lot_number} Image ${index + 1}`}
                  className="h-48 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleImageClick(lot.images, index)}
                />
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-6">
            {metadata?.translated_title && (
              <div>
                <h4 className="font-medium text-gray-800 underline">Translated Title</h4>
                <p className="whitespace-pre-wrap">{metadata.translated_title}</p>
              </div>
            )}
            {metadata?.translated_description && (
              <div>
                <h4 className="font-medium text-gray-800 underline">Translated Description</h4>
                <p className="whitespace-pre-wrap">{metadata.translated_description}</p>
              </div>
            )}
            {metadata?.raw_title && (
              <div>
                <h4 className="font-medium text-gray-800 underline">Original Title</h4>
                <p className="whitespace-pre-wrap">{metadata.raw_title}</p>
              </div>
            )}
            {metadata?.raw_description && (
              <div>
                <h4 className="font-medium text-gray-800 underline">Original Description</h4>
                <p className="whitespace-pre-wrap">{metadata.raw_description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {selectedImage && (
      <ImageViewerModal
        selectedImage={selectedImage}
        onClose={closeImageModal}
        onNavigate={handleNavigate}
      />
    )}
    </>
  );
}
